
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { PasswordStrengthMeter } from '@/components/ui/password-strength-meter';
import { Mail, Lock, User, UserPlus, Upload, Play } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as jose from 'jose';
import { Logo } from '@/components/Logo';
import { debugLog } from '@/lib/videoPlayerDebug';
import { PROMO_HEADLINE, PromoPointsList } from '@/components/promoContent';
import type { ProjectPermission } from '@/types/Permissions';
import {
  getShareLinkPreview,
  getShareLinkTokenFromPath,
  SHARE_LINK_UNAVAILABLE_MESSAGES,
  type ShareLinkPreview,
} from '@/lib/shareLinks';

/**
 * Left-hand promotional panel for the auth screen: the app's tagline + feature
 * bullets. Hidden on small screens. Copy is taken from index.html / Index.tsx /
 * CLAUDE.md — no new marketing copy invented.
 */
const PromoPanel = () => (
  <div className="hidden lg:flex flex-col max-w-xl">
    <h2 className="text-4xl font-extrabold text-white leading-tight drop-shadow-lg">
      {PROMO_HEADLINE}
    </h2>
    <PromoPointsList className="space-y-4 mt-7" />
  </div>
);

/** Auth-card subtitle for a visitor who arrived on a shareable project link. */
function getShareLinkSubtitle(preview: ShareLinkPreview | null, isSignUp: boolean): string {
  if (!preview) return `${isSignUp ? 'Create an account' : 'Sign in'} to join this project`;
  if (preview.status === 'valid') return `You've been invited to "${preview.projectName}"`;
  return SHARE_LINK_UNAVAILABLE_MESSAGES[preview.status];
}

/** Steps 2 and 3 never change; step 1 follows the form's mode. */
const SHARE_LINK_LATER_STEPS = [
  { icon: Upload, title: 'Upload', text: 'Add your photos and videos. They sort themselves into place by date' },
  { icon: Play, title: 'Watch', text: "Play the whole movie, with everyone's clips in order" },
];

/**
 * "How this works" for a visitor on a live share link: the three things they
 * came to do. Step 1 follows the form's mode so it never contradicts the button.
 */
const ShareLinkSteps = ({ isSignUp }: { isSignUp: boolean }) => {
  const steps = [
    {
      icon: UserPlus,
      title: isSignUp ? 'Create a free account' : 'Sign in',
      text: isSignUp ? 'Just your name, email and a password' : 'Use your existing Life Movie Maker account',
    },
    ...SHARE_LINK_LATER_STEPS,
  ];
  return (
    <ol className="space-y-3 mb-6 text-left">
      {steps.map(({ icon: Icon, title, text }, i) => (
        <li key={title} className="flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-white/20 border border-white/30 flex items-center justify-center font-bold text-white">
            {i + 1}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-white leading-snug flex items-center gap-1.5">
              <Icon className="w-4 h-4" />
              {title}
            </p>
            <p className="text-white/70 text-sm leading-snug">{text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // A shareable project link (/join/{token}). Signing in or up leaves the URL
  // alone, so the authenticated router lands on JoinProject, which does the join.
  const shareToken = getShareLinkTokenFromPath(location.pathname);
  const [sharePreview, setSharePreview] = useState<ShareLinkPreview | null>(null);
  // Share-link visitors are mostly new, so they start on "Create a free account".
  const [isSignUp, setIsSignUp] = useState(() => !!shareToken);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{
    projectId: string;
    projectName: string;
    inviterName: string;
    permission: string;
    email?: string;
  } | null>(null);

  // Check for invite token in URL params
  useEffect(() => {
    const token = searchParams.get('invite');
    if (token) {
      setInviteToken(token);
      setIsSignUp(true); // Switch to signup mode for invites

      // Try to decode the invite to show project info
      try {
        // decodeJwt does NOT verify — this is display-only info for the signup
        // screen (the token is properly verified before it grants anything).
        // Shape it explicitly rather than storing a bare JWTPayload.
        const decoded = jose.decodeJwt(token) as Record<string, unknown>;
        setInviteInfo({
          projectId: String(decoded.projectId ?? ''),
          projectName: String(decoded.projectName ?? ''),
          inviterName: String(decoded.inviterName ?? ''),
          permission: String(decoded.permission ?? ''),
          email: decoded.email ? String(decoded.email) : undefined,
        });
        // Pre-fill email from invite token and make it readonly
        if (decoded.email) {
          setEmail(decoded.email as string);
        }
        setMessage(`You've been invited to join "${decoded.projectName}" by ${decoded.inviterName}`);
      } catch (error) {
        console.error('Failed to decode invite token:', error);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!shareToken) return;
    let cancelled = false;
    getShareLinkPreview(shareToken)
      .then((preview) => { if (!cancelled) setSharePreview(preview); })
      .catch((err) => console.error('[ShareLink] Failed to load share link preview:', err));
    return () => { cancelled = true; };
  }, [shareToken]);

  // Process invite token for EXISTING users signing in
  // (New users are handled automatically by database trigger)
  const processInvite = async (token: string) => {
    try {
      console.log('Processing invite for existing user...');

      // Verify and decode the invite token
      const inviteSecret = import.meta.env.VITE_INVITE_SIGNING_SECRET;
      if (!inviteSecret) {
        throw new Error('Invite configuration error');
      }

      const secret = new TextEncoder().encode(inviteSecret);
      const { payload } = await jose.jwtVerify(token, secret);

      const { projectId, permission: rawPermission, email: inviteEmail } = payload as {
        projectId: string;
        permission: string;
        email: string;
      };

      // The token is signature-verified, but the permission inside it is still
      // just a string as far as types go, and it goes straight into a CHECK-
      // constrained column. Validate rather than insert whatever arrives: a
      // malformed invite should fall back to the least privilege, not fail
      // mid-insert or grant something unintended.
      const ALLOWED_PERMISSIONS: ProjectPermission[] = ['owner', 'editor', 'contributor', 'viewer'];
      const permission: ProjectPermission =
        ALLOWED_PERMISSIONS.includes(rawPermission as ProjectPermission)
          ? (rawPermission as ProjectPermission)
          : 'viewer';
      if (permission !== rawPermission) {
        console.error('[Invite] Unrecognised permission in token, defaulting to viewer:', rawPermission);
      }
      const normalizedInviteEmail = inviteEmail.toLowerCase();

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Try to update pending invite (RLS policy now allows this)
      const { error: updateError } = await supabase
        .from('project_members')
        .update({
          user_id: user.id,
          invite_accepted_at: new Date().toISOString()
        })
        .eq('project_id', projectId)
        .eq('invited_email', normalizedInviteEmail)
        .is('user_id', null);

      if (updateError) {
        console.error('Error updating pending invite:', updateError);

        // If no pending invite exists, insert new member
        const { error: insertError } = await supabase
          .from('project_members')
          .insert({
            project_id: projectId,
            user_id: user.id,
            permission: permission
          });

        if (insertError && insertError.code !== '23505') {
          throw insertError;
        }

        // Clean up any pending invites
        await supabase
          .from('project_members')
          .delete()
          .eq('project_id', projectId)
          .eq('invited_email', normalizedInviteEmail)
          .is('user_id', null);
      }

      console.log('Invite accepted successfully');

      // Redirect to project
      navigate(`/project/${projectId}`);
    } catch (error) {
      console.error('Error processing invite:', error);

      const err = error as { code?: string; message?: string };

      // Check if token expired
      if (err.code === 'ERR_JWT_EXPIRED') {
        setError('This invite link has expired. Please ask for a new invitation.');
      } else if (err.code === '23505') {
        // Duplicate key - user already a member
        const { payload } = jose.decodeJwt(token);
        const projectId = (payload as { projectId: string }).projectId;
        navigate(`/project/${projectId}`);
      } else {
        setError('Failed to process invite. Please try again or contact support.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!inviteToken) {
      setMessage('');
    }

    try {
      if (isForgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setMessage('Password reset email sent! Check your inbox.');
      } else if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: name
            }
          }
        });
        if (error) throw error;

        // If there's an invite token, the database trigger already accepted it
        // Just navigate to the project - no need to call Edge Function
        if (inviteToken) {
          const decoded = jose.decodeJwt(inviteToken);
          const projectId = decoded.projectId as string;
          navigate(`/project/${projectId}`);
        } else if (!shareToken) {
          navigate('/projects');
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        // If there's an invite token, process it after signin
        if (inviteToken) {
          await processInvite(inviteToken);
        } else if (!shareToken) {
          navigate('/projects');
        }
      }
    } catch (error) {
      const err = error as { message?: string };
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (isForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-md border-white/20 text-white">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Logo size={56} />
            </div>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <p className="text-white/70">Enter your email to receive a reset link</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                  placeholder="Enter your email"
                />
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm">
                  {error}
                </div>
              )}

              {message && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 text-green-200 text-sm">
                  {message}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <div className="text-center">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsForgotPassword(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  Back to Sign In
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600">
      <div className="min-h-screen mx-auto max-w-7xl px-4 py-10 lg:py-16 flex flex-col gap-10 lg:gap-14 justify-center">
        {/* Row 1: logo + (heading/tagline) centered as a group; text stays left-aligned */}
        <div className="hidden lg:flex items-center justify-center gap-8 text-left">
          <Logo size={128} className="shrink-0 [&_img]:shadow-[0_0_40px_8px_rgba(255,255,255,0.95)]" />
          <div>
            <h1 className="text-6xl font-extrabold text-white tracking-tight drop-shadow-lg">
              Life Movie Maker
            </h1>
            <p className="text-white/90 text-xl mt-3">
              Turn your camera roll into instant travel videos or an endless visual story.
            </p>
          </div>
        </div>

        {/* Row 2: promo (copy) | login controls — top-aligned */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Col 1: promotional panel (logo + copy). Hidden on mobile. */}
          <PromoPanel />

          {/* Col 2: login controls */}
          <div className="flex flex-col items-center lg:items-end gap-6">
            {/* Mobile-only brand header (row-1 heading/logo are desktop-only) */}
            <div className="flex lg:hidden flex-col items-center gap-3">
              <Logo size={72} className="[&_img]:shadow-[0_0_40px_8px_rgba(255,255,255,0.95)]" />
              <h1 className="text-3xl font-extrabold text-white tracking-tight drop-shadow-lg">
                Life Movie Maker
              </h1>
            </div>
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-md border-white/20 text-white">
        <CardHeader className="text-center">
          {(inviteToken || shareToken) && (
            <div className="flex justify-center mb-4">
              <UserPlus className="w-12 h-12 text-white" />
            </div>
          )}
          <CardTitle className="text-2xl">
            {inviteToken || shareToken ? 'Join Project' : isSignUp ? 'Create Account' : 'Welcome Back'}
          </CardTitle>
          <p className="text-white/70">
            {inviteToken
              ? `Create an account to join "${inviteInfo?.projectName}"`
              : shareToken
                ? getShareLinkSubtitle(sharePreview, isSignUp)
              : isSignUp
                ? 'Sign up to start creating video collages'
                : 'Sign in to your account'}
          </p>
        </CardHeader>
        <CardContent>
          {shareToken && (!sharePreview || sharePreview.status === 'valid') && (
            <ShareLinkSteps isSignUp={isSignUp} />
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                  placeholder="Enter your name"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={!!inviteToken}
                disabled={!!inviteToken}
                className={`bg-white/20 border-white/30 text-white placeholder:text-white/60 ${inviteToken ? 'cursor-not-allowed opacity-75' : ''}`}
                placeholder="Enter your email"
                title={inviteToken ? 'Email is set from your invitation' : ''}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                placeholder="Enter your password"
                minLength={6}
              />
              {isSignUp && (
                <PasswordStrengthMeter password={password} className="mt-3" />
              )}
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm">
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
              disabled={loading}
            >
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </Button>

            {!isSignUp && (
              <div className="text-center">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-white/70 hover:text-white hover:bg-white/10 text-sm"
                >
                  Forgot Password?
                </Button>
              </div>
            )}

            <div className="pt-2 border-t border-white/20">
              <p className="text-center text-white/80 text-sm mb-2">or</p>
              <Button
                type="button"
                onClick={() => {
                  debugLog.userAction(`User clicked '${isSignUp ? 'Sign in' : 'Create a free account'}' (auth mode toggle)`);
                  setIsSignUp(!isSignUp);
                }}
                className="w-full h-11 bg-white/25 hover:bg-white/40 text-white font-semibold border border-white/40"
              >
                {isSignUp ? 'Sign in' : 'Create a free account'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
